import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useFormState } from '@/hooks/useFormState';
import { useVerificationCode } from '@/hooks/useVerificationCode';
import { forgotPassword } from '@/services/authService';
import { validateForgotPasswordForm, validatePasswordMatch } from '@/utils/authValidation';

import { Button } from '../common/Button';
import { ErrorMessage } from '../common/ErrorMessage';
import { FormInput } from '../common/FormInput';
import { VerificationCodeInput } from '../common/VerificationCodeInput';

interface ForgotPasswordFormData {
  email: string;
  verificationCode: string;
  password: string;
  confirmPassword: string;
}

export const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const {
    isCodeSent,
    countdown,
    isLoading: codeLoading,
    sendCode,
    cleanup,
  } = useVerificationCode();

  const { values, errors, isLoading, setValue, setError, clearError, setLoading, resetErrors } =
    useFormState<ForgotPasswordFormData>({
      initialValues: {
        email: '',
        verificationCode: '',
        password: '',
        confirmPassword: '',
      },
    });

  // 清理定时器
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // 发送验证码
  const handleSendCode = async () => {
    const result = await sendCode(values.email);
    if (!result.success && result.error) {
      setError('email', result.error);
    } else {
      clearError('email');
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    const validationErrors = validateForgotPasswordForm(values);

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, error]) => {
        setError(field, error);
      });
      return;
    } else {
      resetErrors();
    }

    try {
      setLoading(true);
      const response = await forgotPassword({
        email: values.email,
        code: values.verificationCode,
        newPassword: values.password,
        confirmPassword: values.confirmPassword,
      });

      if (response.status === 200) {
        toast.success(t('auth.forgotPassword.successMessage'));
        window.location.href = '/login';
      } else {
        setError('general', response.message);
        toast.error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.message || t('auth.forgotPassword.errorMessage');
      setError('general', errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleResetPassword();
      }}
    >
      {/* General error message */}
      <ErrorMessage error={errors.general} />

      {/* Email input */}
      <FormInput
        type="email"
        name="email"
        id="forgot-password-email"
        autoComplete="email"
        tabIndex={1}
        value={values.email}
        onChange={(e) => {
          setValue('email', e.target.value);
          clearError('email');
          clearError('general');
        }}
        placeholder={t('auth.forgotPassword.emailPlaceholder')}
        error={errors.email}
      />

      {/* Verification code input */}
      <VerificationCodeInput
        value={values.verificationCode}
        onChange={(value) => {
          setValue('verificationCode', value);
          clearError('verificationCode');
          clearError('general');
        }}
        onSendCode={handleSendCode}
        error={errors.verificationCode}
        isCodeSent={isCodeSent}
        countdown={countdown}
        isLoading={codeLoading}
        disabled={!values.email}
        tabIndex={2}
      />

      {/* New password input */}
      <FormInput
        type="password"
        name="password"
        id="forgot-password-new-password"
        autoComplete="new-password"
        tabIndex={3}
        value={values.password}
        onChange={(e) => {
          const newPassword = e.target.value;
          setValue('password', newPassword);
          clearError('password');
          clearError('general');

          // 实时检查密码匹配（如果确认密码已输入）
          if (values.confirmPassword) {
            const matchError = validatePasswordMatch(newPassword, values.confirmPassword);
            if (matchError) {
              setError('confirmPassword', matchError);
            } else {
              clearError('confirmPassword');
            }
          }
        }}
        placeholder={t('auth.forgotPassword.passwordPlaceholder')}
        error={errors.password}
      />

      {/* Confirm password input */}
      <FormInput
        type="password"
        name="confirmPassword"
        id="forgot-password-confirm-password"
        autoComplete="new-password"
        tabIndex={4}
        value={values.confirmPassword}
        onChange={(e) => {
          const newConfirmPassword = e.target.value;
          setValue('confirmPassword', newConfirmPassword);
          clearError('general');

          // 实时检查密码匹配
          const matchError = validatePasswordMatch(values.password, newConfirmPassword);
          if (matchError) {
            setError('confirmPassword', matchError);
          } else {
            clearError('confirmPassword');
          }
        }}
        placeholder={t('auth.forgotPassword.confirmPasswordPlaceholder')}
        error={errors.confirmPassword}
      />

      {/* Reset password button */}
      <div className="">
        <Button
          onClick={handleResetPassword}
          disabled={isLoading}
          isLoading={isLoading}
          tabIndex={5}
        >
          {t('auth.forgotPassword.resetPasswordButton')}
        </Button>
      </div>

      {/* Back to login link */}
      <div className="mt-[12px] flex justify-center">
        <Link to="/login" className=" text-black hover:text-black hover:opacity-60">
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </div>

      {/* Terms of service text */}
      <div className="mt-[20px] text-center text-[12px] font-medium leading-[150%] text-black">
        {t('auth.forgotPassword.termsText')}{' '}
        <a
          href="/policies/terms-of-use"
          target="_blank"
          data-link="terms"
          className="auth-link cursor-pointer text-[rgb(37,38,38)] hover:text-[rgb(37,38,38,.6)]"
          rel="noreferrer"
        >
          {t('auth.forgotPassword.termsOfService')}
        </a>{' '}
        {t('auth.forgotPassword.and')}
        <br />
        {t('auth.forgotPassword.acknowledgeText')}{' '}
        <a
          href="/policies/privacy-policy"
          target="_blank"
          data-link="privacy"
          className="auth-link cursor-pointer text-[rgb(37,38,38)] hover:text-[rgb(37,38,38,.6)]"
          rel="noreferrer"
        >
          {t('auth.forgotPassword.privacyPolicy')}
        </a>
      </div>
    </form>
  );
};
